import { capitalize } from '@superbet-group/web.lib.utilities';
import {
    useI18n,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    computed,
    ComputedRef,
    onMounted,
    ref,
} from '@vue/composition-api';

import {
    LimitData,
    LimitStatus,
    LimitType,
    PeriodData,
} from '@core/models/user/Limit';
import { ModalStore } from '@store/modules/ui/shared/modal/modalIndex';
import { useResponsibleGaming } from '../useResponsibleGaming';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { logger } from '@src/app/logger';

const fieldErrorKey = '{periodSmaller} amount cannot be higher than {periodLarger} amount';

export function useShowLimitsModals() {
    const modalStore = useVuexStore<ModalStore>('ui/modal');

    const setModal = modalStore.action('setModal');
    const closeModal = modalStore.action('closeModal');

    const showLimitModal = (payload: LimitModalData) => {
        setModal({
            code: modalCodes.rgSetLimit,
            data: payload,
        });
    };

    const showRemoveLimitModal = (type: LimitType, limits: Partial<Record<keyof LimitData, number>>) => {
        setModal({
            code: modalCodes.rgRemoveLimits,
            data: {
                type,
                limits,
            },
        });
    };

    const showConfirmLimitsModal = async (payload: ConfirmLimitModalData) => {
        await closeModal();
        await setModal({
            code: modalCodes.rgConfirmLimits,
            data: payload,
        });
    };

    const showCanceLimitsModal = (payload: CancelLimitsModal) => {
        setModal({
            code: modalCodes.rgCancelLimit,
            data: payload,
        });
    };

    return {
        showLimitModal,
        showRemoveLimitModal,
        showConfirmLimitsModal,
        showCanceLimitsModal,
    };
}

export function useRemoveLimitsModalData() {
    const modalStore = useVuexStore<ModalStore>('ui/modal');

    const modalData = modalStore.getter('modalData');

    return {
        type: computed(() => modalData.value?.type as LimitType),
        limits: computed(() => modalData.value?.limits as Record<keyof LimitData, number>),
    };
}

export function useChangeLimitModalData() {
    const validationError = ref<string>();

    const { translate } = useI18n();
    const { showConfirmLimitsModal } = useShowLimitsModals();
    const modalStore = useVuexStore<ModalStore>('ui/modal');

    const modalData = modalStore.getter('modalData') as ComputedRef<LimitModalData | undefined>;

    const currentLimits = computed(() => modalData.value?.currentLimits);
    const limitType = computed(() => modalData.value?.type);
    const limitKey = computed(() => modalData.value?.limit);
    const canDelete = computed(() => !!modalData.value?.canDelete);
    const unitOverride = computed(() => modalData.value?.unitOverride);
    const initialValue = computed(() => modalData.value?.initialValue ?? currentLimits.value?.[limitKey.value!]?.amount ?? 0);
    const currentValue = computed(() => currentLimits.value?.[limitKey.value!]?.amount ?? 0);

    const typeInfix = computed(() => {
        switch (limitType.value) {
            case LimitType.TotalWager:
                return 'totalWager';
            case LimitType.Loss:
                return 'loss';
            case LimitType.Session:
                return 'session';
            case LimitType.Deposit:
                return 'deposit';
            default:
                return '';
        }
    });

    const periodInfix = computed(() => {
        switch (limitKey.value) {
            case 'day':
                return 'daily';
            case 'week':
                return 'weekly';
            case 'month':
                return 'monthly';
            default:
                return '';
        }
    });

    const title = computed(() =>
        `pam.responsibleGaming.limits.${typeInfix.value}.${periodInfix.value}.title`,
    );

    const label = computed(() => `pam.responsibleGaming.limits.${periodInfix.value}.amount`);

    const produceFieldError = (smallerPeriod: string, largerPeriod: string) => {
        return capitalize(translate(fieldErrorKey, {
            periodSmaller: translate(smallerPeriod).toString(),
            periodLarger: translate(largerPeriod).toString(),
        }));
    };

    const getCurrentLimit = (limit: PeriodData | null | undefined): number | undefined => {
        const isPending = limit?.status === LimitStatus.Inactive;
        if (isPending) {
            return limit?.previousAmount;
        } else {
            return limit?.amount;
        }
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const getValidationError = (amount: number): string | undefined => {
        if (amount === 0) {
            return translate(
                'Entered limit can`t be 00.00. If you want to remove a limit, please un-tick the according box.',
            ).toString();
        }

        const currentDailyLimit = getCurrentLimit(currentLimits.value?.day);
        const currentWeeklyLimit = getCurrentLimit(currentLimits.value?.week);
        const currentMonthlyLimit = getCurrentLimit(currentLimits.value?.month);

        if (limitKey.value === 'day') {
            if (currentWeeklyLimit != null && amount > currentWeeklyLimit) {
                return produceFieldError('day', 'week');
            } else if (currentMonthlyLimit != null && amount > currentMonthlyLimit) {
                return produceFieldError('day', 'month');
            }
        } else if (limitKey.value === 'week') {
            if (currentDailyLimit != null && amount < currentDailyLimit) {
                return produceFieldError('day', 'week');
            } else if (currentMonthlyLimit != null && amount > currentMonthlyLimit) {
                return produceFieldError('week', 'month');
            }
        } else if (limitKey.value === 'month') {
            if (currentDailyLimit != null && amount < currentDailyLimit) {
                return produceFieldError('day', 'month');
            } else if (currentWeeklyLimit != null && amount < currentWeeklyLimit) {
                return produceFieldError('week', 'month');
            }
        }
    };

    const confirmSubmitLimit = (value: number, validate = true) => {
        try {
            const errorMessage = validate ? getValidationError(value) : undefined;

            if (errorMessage) {
                validationError.value = errorMessage;
                return false;
            } else {
                showConfirmLimitsModal({
                    type: limitType.value!,
                    currentLimits: currentLimits.value!,
                    limit: limitKey.value!,
                    value,
                    canDelete: canDelete.value,
                    unitOverride: unitOverride.value,
                });
                return true;
            }
        } catch (error) {
            logger.error(error as Error);
            validationError.value = translate('pam.limitsModal.error').toString();
            return false;
        }
    };

    onMounted(() => {
        validationError.value = modalData.value?.initialError;
    });

    return {
        title,
        label,
        canDelete,
        initialValue,
        currentValue,
        unitOverride,
        validationError,
        confirmSubmitLimit,
    };
}

export function useConfirmLimitModal() {
    const limitTypeTexts: Record<LimitType, string> = {
        [LimitType.TotalWager]: 'totalWagerGenitiv',
        [LimitType.Loss]: 'lossGenitiv',
        [LimitType.Session]: 'sessionGenitiv',
        [LimitType.Deposit]: 'depositGenitiv',
    };

    const isSubmitting = ref<boolean>(false);

    const { translate } = useI18n();
    const {
        refreshLimits,
        setLimits,
    } = useResponsibleGaming();
    const { showLimitModal } = useShowLimitsModals();

    const modalStore = useVuexStore<ModalStore>('ui/modal');
    const modalData = modalStore.getter('modalData') as ComputedRef<ConfirmLimitModalData | undefined>;

    const currentLimits = computed(() => modalData.value?.currentLimits);
    const limitType = computed(() => modalData.value?.type);
    const limitKey = computed(() => modalData.value?.limit);
    const limitValue = computed(() => modalData.value?.value);
    const canDelete = computed(() => modalData.value?.canDelete);
    const unitOverride = computed(() => modalData.value?.unitOverride);
    const previousValue = computed(() => currentLimits.value?.[limitKey.value!]?.amount ?? 0);

    const limitTypeText = computed(() => limitTypeTexts[limitType.value!] ?? '');

    const submitLimit = async () => {
        try {
            isSubmitting.value = true;
            const limits = {
                day: currentLimits.value?.day?.amount,
                week: currentLimits?.value?.week?.amount,
                month: currentLimits.value?.month?.amount,
            };

            limits[limitKey.value!] = limitValue.value;

            await setLimits({
                limits,
                limitType: limitType.value!,
            });
            await refreshLimits();
        } catch (error) {
            const validationError = translate('pam.limitsModal.error').toString();
            showLimitModal({
                currentLimits: currentLimits.value!,
                type: limitType.value!,
                limit: limitKey.value!,
                initialValue: limitValue.value,
                initialError: validationError,
                canDelete: !!canDelete.value,
                unitOverride: unitOverride.value,
            });
        } finally {
            isSubmitting.value = false;
        }
    };

    return {
        limitValue,
        previousValue,
        modalData,
        limitTypeText,
        submitLimit,
    };
}

type LimitModalData = {
    currentLimits: LimitData;
    type: LimitType;
    limit: keyof LimitData;
    initialValue?: number;
    initialError?: string;
    canDelete: boolean;
    unitOverride?: string;
};

type ConfirmLimitModalData = {
    currentLimits: LimitData;
    type: LimitType;
    limit: keyof LimitData;
    value: number;
    canDelete: boolean;
    unitOverride?: string;
};

type CancelLimitsModal = {
    currentLimits: LimitData;
    limitKey?: keyof LimitData;
};
